import { ClientEvents } from 'common/events/ClientEvents';
import { formatDateTimeUrlDate } from 'common/UrlDate';
import { first } from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import { DishFirstExploreWidthLimit } from 'src/pages/consumer/dish-first/explore/components/SharedStyles';
import { DynamicImage } from 'src/shared/design-system/DynamicImage';
import { ChevronRight } from 'src/shared/design-system/Icon';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useSelector } from 'src/store';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { BorderRadius, Colors, Font, FontSize, FontWeight, ScreenWidth, Spacing } from 'web-common/src/shared/styles';
import { MiniBanner } from './components/MiniBanner';

const Container = styled.div`
  position: relative;
  width: 100%;
  background: ${Colors.JACKFRUIT_LITE_30};
  border-radius: ${BorderRadius.M};
  overflow: hidden;
  padding: ${Spacing.DOUBLE_QUARTER} ${Spacing.DOUBLE_HALF} ${Spacing.SINGLE_QUARTER};

  @media screen and (max-width: ${ScreenWidth.EXTRA_SMALL}) {
    padding: ${Spacing.SINGLE} 0 ${Spacing.QUADRUPLE};
  }
`;

const TextContainer = styled.div`
  position: relative;

  @media screen and (max-width: ${ScreenWidth.EXTRA_SMALL}) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const Title = styled.p`
  color: ${Colors.GRAY_DARK_30};
  font-size: 32px;
  font-weight: 700;
  font-family: ${Font.Family.SANS};
  margin-bottom: ${Spacing.EIGHTH};

  @media screen and (max-width: ${ScreenWidth.EXTRA_SMALL}) {
    font-size: 26px;
  }
`;

const Subtitle = styled.p`
  color: ${Colors.GRAY_DARK_20};
  font-size: ${FontSize.S};
  font-weight: ${FontWeight.MEDIUM};
  font-family: ${Font.Family.SANS};
`;

const LinkButton = styled.button`
  color: ${Colors.RADISH};
  padding: 10px 0;
  border: none;
  background: none;
  font-weight: ${FontWeight.SEMI_BOLD};
  font-size: ${FontSize.XS};
  display: inline-flex;
  align-items: center;

  @media screen and (max-width: ${ScreenWidth.EXTRA_SMALL}) {
    padding: 0;
  }
`;

const ChevronRightIcon = styled(ChevronRight)`
  margin-left: ${Spacing.SINGLE};
`;

const ImageContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;

  img {
    width: 100% !important;
  }

  @media screen and (min-width: 490px) and (max-width: ${ScreenWidth.EXTRA_SMALL}) {
    img {
      margin-bottom: -16px;
    }
  }

  @media screen and (min-width: ${ScreenWidth.EXTRA_SMALL}) {
    bottom: unset;
    left: unset;
    right: 0;
    top: 0;
  }

  @media screen and (min-width: ${ScreenWidth.SMALL}) and (max-width: ${ScreenWidth.MEDIUM}) {
    width: 70%;

    img {
      object-position: left;
    }
  }
`;

const useGetItTomororwBanner = () => {
  const [, , zipCodeMetaData] = useCachedZipCode();
  const exploreTimeZone: string = zipCodeMetaData.cachedZipCodeData?.zipCode?.timeZone || DateTime.local().zoneName;

  // relevant time values for our display calculations
  const [deliveryDates, setDeliveryDates] = usePersistentDeliveryDates();
  const deliveryDate = first(deliveryDates);
  const [firstDeliveryDate] = useSelector((state) => state.cart.possibleDeliveryDates);
  const exploreNow = DateTime.now().setZone(exploreTimeZone);
  const tomorrowDeliveryDate = formatDateTimeUrlDate(exploreNow.plus({ days: 1 }));

  // calculate if we should show the tomorrow delivery date modal
  const firstDeliveryDateIsTomorrow = firstDeliveryDate === tomorrowDeliveryDate;
  const isNowAfter6pm = exploreNow.toMillis() >= exploreNow.startOf('day').plus({ hours: 18 }).toMillis();
  const userIsViewingTomorrow = deliveryDate === tomorrowDeliveryDate;
  const shouldShow = firstDeliveryDateIsTomorrow && !userIsViewingTomorrow && isNowAfter6pm;

  const tracker = useTracker();
  const trackClick = useCallback(() => {
    setDeliveryDates([tomorrowDeliveryDate]);
    const exploreNow = DateTime.now().setZone(exploreTimeZone);
    const exploreEndOfDay = DateTime.now().setZone(exploreTimeZone).endOf('day');
    tracker.track(ClientEvents.DISH_FIRST_CLICK_NEXT_DAY_BANNER, {
      msBeforeMidnight: exploreEndOfDay.toMillis() - exploreNow.toMillis(),
      nextDayOfWeek: exploreNow.setLocale('us').toFormat('EEEE'),
    });
  }, [tracker, setDeliveryDates, tomorrowDeliveryDate]);

  return { trackClick, shouldShow };
};

export const GetItTomorrowBanner: React.FC = () => {
  const { nearestScreenWidth } = useScreenContext();
  const { shouldShow, trackClick } = useGetItTomororwBanner();

  const imageProps = match(nearestScreenWidth)
    .with(ScreenWidth.EXTRA_LARGE, () => ({
      sourceHeight: 352,
      sourceWidth: 1616,
      height: 176,
      width: 808,
      src: 'https://shef-general.s3-us-west-1.amazonaws.com/assets/img/explore/get-it-tomorrow-banner-desktop.png',
    }))
    .with(ScreenWidth.MEDIUM, () => ({
      sourceHeight: 352,
      sourceWidth: 1376,
      height: 176,
      width: 688,
      src: 'https://shef-general.s3-us-west-1.amazonaws.com/assets/img/explore/get-it-tomorrow-banner-tablet-large.png',
    }))
    .with(ScreenWidth.SMALL, ScreenWidth.LARGE, () => ({
      sourceHeight: 352,
      sourceWidth: 1376,
      height: 176,
      width: 688,
      src: 'https://shef-general.s3-us-west-1.amazonaws.com/assets/img/explore/get-it-tomorrow-banner-tablet.png',
    }))
    .otherwise(() => ({
      sourceHeight: 320,
      sourceWidth: 686,
      src: 'https://shef-general.s3-us-west-1.amazonaws.com/assets/img/explore/get-it-tomorrow-banner-mobile.png',
    }));

  if (!shouldShow) return null;

  return (
    <DishFirstExploreWidthLimit className='mb-10 mt-2 px-4 py-0 sm:-mt-4'>
      <Container>
        <ImageContainer>
          <DynamicImage {...imageProps} />
        </ImageContainer>
        <TextContainer>
          <Title>Get it tomorrow</Title>
          <Subtitle>Limited supply! Order by 9pm</Subtitle>
          <LinkButton onClick={trackClick}>
            Browse dishes
            <ChevronRightIcon width={14} height={14} />
          </LinkButton>
        </TextContainer>
      </Container>
    </DishFirstExploreWidthLimit>
  );
};

export const MiniGetItTomorrowBanner: React.FC<{ className?: string }> = ({ className }) => {
  const { shouldShow, trackClick } = useGetItTomororwBanner();

  if (!shouldShow) return null;

  return (
    <MiniBanner
      className={className}
      bgColorClassName='bg-jackfruit-50'
      supportingText='Limited supply!'
      mainText='Get it tomorrow'
      ctaText='Order by 9pm'
      ctaClick={trackClick}
      graphic={
        <DynamicImage
          sourceHeight={1570}
          sourceWidth={1838}
          height={136}
          width={160}
          src={'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/explore/get-it-tomorrow-small-banner.png'}
        />
      }
    />
  );
};
